import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import katex from 'katex';

import { MatTabsModule } from '@angular/material/tabs';
import { ButtonComponent } from '@examdojo/core/button';
import type { ThemeColorName } from '@examdojo/styles';

import { TreeChartComponent } from '../../../probability/tree/tree/tree-chart.component';
import type { TreePath } from '../../../probability/tree/tree/tree.model';
import { binomialMatchingPaths, binomialTreeParams } from '../../../probability/tree/tree/tree.presets';
import { PascalsTriangleComponent } from './pascals-triangle.component';
import {
  DEFAULT_PASCALS_TRIANGLE_PARAMS,
  DEFAULT_PROPERTIES,
  PascalsTriangleHighlightFn,
  PascalsTriangleParams,
  PascalsTrianglePropertyDef,
} from './pascals-triangle.model';

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html', trust: true });
}

/** Ordered palette of theme colors used for symmetric coefficient coloring. */
const EXPANSION_COLORS: readonly ThemeColorName[] = ['teal', 'amber', 'purple', 'pink', 'green', 'indigo', 'orange'];

/**
 * KaTeX needs actual hex values — these must match the light-mode --{color}-500 tokens
 * from _root-variables-colors.scss.
 */
const COLOR_HEX_500: Record<ThemeColorName, string> = {
  sky: '#0d95ea',
  teal: '#10bba2',
  amber: '#f9a107',
  purple: '#9e5af2',
  pink: '#e94b7a',
  red: '#f73c42',
  green: '#27c027',
  indigo: '#5b66f9',
  orange: '#f77409',
  neutral: '#706f6d',
};

function buildExpansionCoefficients(n: number): number[] {
  const row: number[] = [1];
  for (let r = 1; r <= n; r++) {
    row.push((row[r - 1] * (n - r + 1)) / r);
  }
  return row;
}

/** Muted hex for graying out inactive terms. */
const INACTIVE_HEX = '#c0c0c0';

function buildExpressionLatex(n: number, hasActive: boolean): string {
  if (hasActive) {
    return `(a + b)^{\\textcolor{${COLOR_HEX_500['sky']}}{${n}}}`;
  }
  return `(a + b)^{${n}}`;
}

function buildProductLatex(n: number): string {
  if (n === 0) {
    return '= 1';
  }
  if (n === 1) {
    return '= (a+b)';
  }
  const factor = '(a+b)';
  if (n <= 4) {
    return `= ${factor.repeat(n)}`;
  }
  return `= \\underbrace{${factor}${factor}\\cdots${factor}}_{${n}\\text{ factors}}`;
}

function getSymmetricColorName(n: number, r: number): ThemeColorName {
  const idx = Math.min(r, n - r);
  return EXPANSION_COLORS[idx % EXPANSION_COLORS.length];
}

function buildExpandedLatex(n: number, activeR: number | null): string {
  if (n === 0) {
    return '= 1';
  }

  const coeffs = buildExpansionCoefficients(n);
  const nHex = COLOR_HEX_500['sky'];
  const rHex = COLOR_HEX_500['red'];
  const terms: string[] = [];

  for (let r = 0; r <= n; r++) {
    const c = coeffs[r];
    const aPow = n - r;
    const bPow = r;
    const isActive = activeR === r;
    const isInactive = activeR !== null && !isActive;

    const coeffHex = activeR === null || isActive ? COLOR_HEX_500[getSymmetricColorName(n, r)] : null;
    const term_coeff = coeffHex
      ? `\\htmlData{anchor=term-${r}}{\\textcolor{${coeffHex}}{${c}}}`
      : `\\htmlData{anchor=term-${r}}{\\textcolor{${INACTIVE_HEX}}{${c}}}`;

    let term = term_coeff;

    if (aPow > 0) {
      if (aPow > 1) {
        // Only color the exponent sky when it equals n (i.e. r=0)
        const exp = isActive && aPow === n ? `\\textcolor{${nHex}}{${aPow}}` : `${aPow}`;
        const part = `a^{${exp}}`;
        term += isInactive ? `\\textcolor{${INACTIVE_HEX}}{${part}}` : part;
      } else {
        term += isInactive ? `\\textcolor{${INACTIVE_HEX}}{a}` : 'a';
      }
    }

    if (bPow > 0) {
      if (bPow > 1) {
        const exp = isActive ? `\\textcolor{${rHex}}{${bPow}}` : `${bPow}`;
        const part = `b^{${exp}}`;
        term += isInactive ? `\\textcolor{${INACTIVE_HEX}}{${part}}` : part;
      } else {
        term += isInactive ? `\\textcolor{${INACTIVE_HEX}}{b}` : 'b';
      }
    }

    terms.push(term);
  }

  const plus = activeR !== null ? `\\textcolor{${INACTIVE_HEX}}{+}` : '+';
  return `= ${terms.join(` ${plus} `)}`;
}

function factorial(n: number): bigint {
  let result = 1n;
  for (let i = 2n; i <= BigInt(n); i++) {
    result *= i;
  }
  return result;
}

function buildExplanationParts(n: number, r: number): Array<{ label: string; text?: string; latex?: string }> {
  const coeffs = buildExpansionCoefficients(n);
  const c = coeffs[r];
  const aPow = n - r;
  const bPow = r;
  const nHex = COLOR_HEX_500['sky'];
  const rHex = COLOR_HEX_500['red'];
  const colorHex = COLOR_HEX_500[getSymmetricColorName(n, r)];

  // Simplified result (e.g. 5a⁴b) — always show coefficient colored
  const simplifiedParts: string[] = [`\\textcolor{${colorHex}}{${c}}`];
  if (aPow > 1) {
    simplifiedParts.push(`a^{\\textcolor{${nHex}}{${aPow}}}`);
  } else if (aPow === 1) {
    simplifiedParts.push('a');
  }
  if (bPow > 1) {
    simplifiedParts.push(`b^{\\textcolor{${rHex}}{${bPow}}}`);
  } else if (bPow === 1) {
    simplifiedParts.push('b');
  }
  const simplified = simplifiedParts.join('');

  // General Term — formula + simplified result on one line
  const generalLatex =
    `r = \\textcolor{${rHex}}{${r}}\\text{: }\\quad ` +
    `\\dbinom{\\textcolor{${nHex}}{${n}}}{\\textcolor{${rHex}}{${r}}}` +
    ` \\cdot ` +
    `a^{\\textcolor{${nHex}}{${n}}-\\textcolor{${rHex}}{${r}}} \\cdot b^{\\textcolor{${rHex}}{${r}}}` +
    ` = ${simplified}`;

  // Part 1 — Coefficient: how many ways?
  const factorWord = r === 1 ? 'factor' : 'factors';
  const rl = renderLatex(`\\textcolor{${rHex}}{${r}}`);
  const nl = renderLatex(`\\textcolor{${nHex}}{${n}}`);
  const bl = renderLatex('b');
  const al = renderLatex('a');
  const part1Text = `How many ways can we choose ${rl} ${factorWord} ` + `(out of ${nl}) to contribute ${bl}?`;
  let part1Latex =
    `\\dbinom{\\textcolor{${nHex}}{${n}}}{\\textcolor{${rHex}}{${r}}}` +
    ` = \\frac{\\textcolor{${nHex}}{${n}}!}{\\textcolor{${rHex}}{${r}}!\\,(\\textcolor{${nHex}}{${n}}-\\textcolor{${rHex}}{${r}})!}` +
    ` = \\frac{\\textcolor{${nHex}}{${n}}!}{\\textcolor{${rHex}}{${r}}!\\cdot ${aPow}!}`;
  if (n <= 10) {
    const nFact = factorial(n);
    const rFact = factorial(r);
    const nrFact = factorial(aPow);
    part1Latex += ` = \\frac{${nFact}}{${rFact} \\cdot ${nrFact}}`;
  }
  part1Latex += ` = \\textcolor{${colorHex}}{${c}}`;

  // Part 2 — Powers: what does each choice produce?
  const remainingWord = aPow === 1 ? 'factor' : 'factors';
  const aPowl = renderLatex(`${aPow}`);
  const part2Text =
    `We pick ${bl} from ${rl} ${factorWord} ` + `and ${al} from the remaining ${aPowl} ${remainingWord}.`;
  const part2Latex =
    `a^{\\textcolor{${nHex}}{${n}}-\\textcolor{${rHex}}{${r}}} \\cdot b^{\\textcolor{${rHex}}{${r}}}` +
    ` = a^{\\textcolor{${nHex}}{${aPow}}} \\cdot b^{\\textcolor{${rHex}}{${bPow}}}`;

  return [
    { label: '', latex: generalLatex },
    { label: 'Part 1: Coefficient', text: part1Text, latex: part1Latex },
    { label: 'Part 2: Powers', text: part2Text, latex: part2Latex },
  ];
}

function buildSymbolicLatex(n: number, activeR: number | null): string {
  const nHex = COLOR_HEX_500['sky'];
  const rHex = COLOR_HEX_500['red'];

  if (n === 0) {
    const isActive = activeR === 0;
    const isInactive = activeR !== null && !isActive;
    const binomHex = activeR === null || isActive ? COLOR_HEX_500[getSymmetricColorName(0, 0)] : null;
    const binomInner = isActive ? `\\binom{\\textcolor{${nHex}}{0}}{\\textcolor{${rHex}}{0}}` : `\\binom{0}{0}`;
    const binom =
      binomHex && !isActive
        ? `\\htmlData{anchor=sym-0}{\\textcolor{${binomHex}}{${binomInner}}}`
        : isInactive
          ? `\\htmlData{anchor=sym-0}{\\textcolor{${INACTIVE_HEX}}{${binomInner}}}`
          : `\\htmlData{anchor=sym-0}{${binomInner}}`;
    const aPart = isActive
      ? `a^{\\textcolor{${nHex}}{0}}`
      : isInactive
        ? `\\textcolor{${INACTIVE_HEX}}{a^{0}}`
        : 'a^{0}';
    const bPart = isActive
      ? `b^{\\textcolor{${rHex}}{0}}`
      : isInactive
        ? `\\textcolor{${INACTIVE_HEX}}{b^{0}}`
        : 'b^{0}';
    const dot = isInactive ? `\\textcolor{${INACTIVE_HEX}}{\\cdot}` : '\\cdot';
    return `= ${binom}\\!${dot} ${aPart} ${dot} ${bPart}`;
  }

  const terms: string[] = [];

  for (let r = 0; r <= n; r++) {
    const isActive = activeR === r;
    const isInactive = activeR !== null && !isActive;
    const aPow = n - r;
    const bPow = r;

    const binomHex = activeR === null || isActive ? COLOR_HEX_500[getSymmetricColorName(n, r)] : null;
    const binomInner = isActive
      ? `\\dbinom{\\textcolor{${nHex}}{${n}}}{\\textcolor{${rHex}}{${r}}}`
      : `\\binom{${n}}{${r}}`;
    const binom =
      binomHex && !isActive
        ? `\\htmlData{anchor=sym-${r}}{\\textcolor{${binomHex}}{${binomInner}}}`
        : isInactive
          ? `\\htmlData{anchor=sym-${r}}{\\textcolor{${INACTIVE_HEX}}{${binomInner}}}`
          : `\\htmlData{anchor=sym-${r}}{${binomInner}}`;
    const aExp = isActive && aPow === n ? `\\textcolor{${nHex}}{${aPow}}` : `${aPow}`;
    const aPart = isActive ? `a^{${aExp}}` : isInactive ? `\\textcolor{${INACTIVE_HEX}}{a^{${aPow}}}` : `a^{${aPow}}`;
    const bPart = isActive
      ? `b^{\\textcolor{${rHex}}{${bPow}}}`
      : isInactive
        ? `\\textcolor{${INACTIVE_HEX}}{b^{${bPow}}}`
        : `b^{${bPow}}`;
    const dot = isInactive ? `\\textcolor{${INACTIVE_HEX}}{\\cdot}` : '\\cdot';

    terms.push(`${binom}\\!${dot} ${aPart} ${dot} ${bPart}`);
  }

  const plus = activeR !== null ? `\\textcolor{${INACTIVE_HEX}}{+}` : '+';
  return `= ${terms.join(` ${plus} `)}`;
}

@Component({
  selector: 'dojo-pascals-triangle-explain',
  standalone: true,
  imports: [PascalsTriangleComponent, TreeChartComponent, MatTabsModule, ButtonComponent],
  templateUrl: './pascals-triangle-explain.component.html',
  styleUrl: './pascals-triangle-explain.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class PascalsTriangleExplainComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input<PascalsTriangleParams>(DEFAULT_PASCALS_TRIANGLE_PARAMS);
  readonly height = input<number | null>(null);
  readonly properties = input<readonly PascalsTrianglePropertyDef[]>(DEFAULT_PROPERTIES);
  readonly showRowSlider = input(true);
  readonly showProperties = input(true);
  readonly showExpansion = input(false);
  readonly showTree = input(false);

  readonly rows = signal(DEFAULT_PASCALS_TRIANGLE_PARAMS.rows);

  private readonly syncRows = effect(() => {
    this.rows.set(this.params().rows);
  });

  readonly effectiveParams = computed<PascalsTriangleParams>(() => ({
    ...this.params(),
    rows: this.rows(),
  }));

  readonly activePropertyId = signal<string | null>(null);
  readonly activeR = signal<number | null>(null);
  readonly activeDetailTab = signal(0);

  readonly activeCell = computed(() => {
    const r = this.activeR();
    return r !== null ? { n: this.power(), r } : null;
  });

  readonly activeColorName = computed<ThemeColorName>(() => {
    const r = this.activeR();
    return r !== null ? getSymmetricColorName(this.power(), r) : 'sky';
  });

  readonly hoveredCell = computed(() => {
    const r = this.hoveredExpansionR();
    return r !== null ? { n: this.power(), r } : null;
  });

  private readonly propertyHighlightFn = computed<PascalsTriangleHighlightFn | null>(() => {
    const id = this.activePropertyId();
    if (!id) {
      return null;
    }
    const prop = this.properties().find((p) => p.id === id);
    return prop?.highlight ?? null;
  });

  private readonly expansionHighlightFn = computed<PascalsTriangleHighlightFn | null>(() => {
    if (!this.showExpansion()) {
      return null;
    }
    const n = this.power();
    const ar = this.activeR();
    const hr = this.hoveredExpansionR();
    return (row: number, r: number) => {
      if (row !== n) {
        return null;
      }
      if (ar !== null && r !== ar && r !== hr) {
        return null;
      }
      return getSymmetricColorName(n, r);
    };
  });

  readonly highlightFn = computed<PascalsTriangleHighlightFn | null>(() => {
    // Property hover takes priority over expansion highlight
    return this.propertyHighlightFn() ?? this.expansionHighlightFn();
  });

  readonly renderedProperties = computed(
    (): ReadonlyArray<{ id: string; propertyHtml: SafeHtml; meaning: string; exampleHtml: SafeHtml }> =>
      this.properties().map((p) => ({
        id: p.id,
        propertyHtml: this.sanitizer.bypassSecurityTrustHtml(renderLatex(p.propertyLatex)),
        meaning: p.meaning,
        exampleHtml: this.sanitizer.bypassSecurityTrustHtml(renderLatex(p.exampleLatex)),
      })),
  );

  readonly power = computed(() => this.rows() - 1);

  readonly expressionHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(buildExpressionLatex(this.power(), this.activeR() !== null))),
  );

  readonly binomialTheoremGeneralHtml = this.sanitizer.bypassSecurityTrustHtml(
    renderLatex(`(a + b)^{n} = \\sum_{r=0}^{n} \\binom{n}{r}\\, a^{n-r}\\, b^{r}`),
  );

  readonly productHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(buildProductLatex(this.power()))),
  );

  readonly symbolicHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(buildSymbolicLatex(this.power(), this.activeR()))),
  );

  readonly expandedHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(buildExpandedLatex(this.power(), this.activeR()))),
  );

  private readonly allExplanationParts = computed(() => {
    const r = this.activeR();
    if (r === null) {
      return null;
    }
    return buildExplanationParts(this.power(), r).map((part) => ({
      label: part.label,
      textHtml: part.text ? this.sanitizer.bypassSecurityTrustHtml(part.text) : null,
      formulaHtml: part.latex ? this.sanitizer.bypassSecurityTrustHtml(renderLatex(part.latex)) : null,
    }));
  });

  readonly generalTermHtml = computed(() => {
    const parts = this.allExplanationParts();
    return parts?.[0]?.formulaHtml ?? null;
  });

  readonly explanationParts = computed(() => {
    const parts = this.allExplanationParts();
    return parts?.slice(1) ?? null;
  });

  readonly termBreakdownHintHtml = computed(() => {
    const r = this.activeR();
    if (r === null) {
      return null;
    }
    const n = this.power();
    const rHex = COLOR_HEX_500['red'];
    const rl = renderLatex(`\\textcolor{${rHex}}{${r}}`);
    const nr = renderLatex(`${n - r}`);
    const bWord = r === 1 ? 'factor' : 'factors';
    const aWord = n - r === 1 ? 'factor' : 'factors';
    return this.sanitizer.bypassSecurityTrustHtml(
      `You selected the ${renderLatex(`r = \\textcolor{${rHex}}{${r}}`)} term. ` +
        `This means we pick ${renderLatex('b')} from ${rl} ${bWord} ` +
        `and ${renderLatex('a')} from the remaining ${nr} ${aWord}.`,
    );
  });

  readonly treeExplanationHtml = computed(() => {
    const r = this.activeR();
    if (r === null) {
      return null;
    }
    const n = this.power();
    const coeffs = buildExpansionCoefficients(n);
    const c = coeffs[r];
    const totalPaths = Math.pow(2, n);
    const rHex = COLOR_HEX_500['red'];
    const colorHex = COLOR_HEX_500[getSymmetricColorName(n, r)];
    const bWord = r === 1 ? 'b' : `b\\text{'s}`;
    return this.sanitizer.bypassSecurityTrustHtml(
      `Each path from top to bottom picks ${renderLatex('a')} or ${renderLatex('b')} at every level. ` +
        `Out of all ${renderLatex(`${totalPaths}`)} paths, exactly ` +
        `${renderLatex(`\\textcolor{${colorHex}}{${c}}`)} contain ` +
        `${renderLatex(`\\textcolor{${rHex}}{${r}}`)} ${renderLatex(bWord)}. These are highlighted below.`,
    );
  });

  private readonly hoveredExpansionR = signal<number | null>(null);

  /**
   * Inline <style> block for hover highlighting — pure CSS, no DOM loops.
   * Targets KaTeX-rendered [data-anchor] elements via attribute selectors.
   */
  readonly hoverStyleHtml = computed(() => {
    const r = this.hoveredExpansionR();
    if (this.activeR() !== null || r === null) {
      return null;
    }
    const colorName = getSymmetricColorName(this.power(), r);
    const css =
      `.pt-expansion [data-anchor="term-${r}"],` +
      `.pt-expansion [data-anchor="sym-${r}"]` +
      `{background:var(--${colorName}-100);border-radius:4px}`;
    return this.sanitizer.bypassSecurityTrustHtml(`<style>${css}</style>`);
  });

  readonly treeHoveredPath = signal<TreePath | null>(null);

  readonly treeParams = computed(() => binomialTreeParams(this.power()));

  readonly treeMatchingPaths = computed(() => {
    const r = this.activeR();
    if (r === null) {
      return [];
    }
    return binomialMatchingPaths(this.power(), r);
  });

  onPowerChange(value: number): void {
    this.rows.set(value + 1);
    this.activeR.set(null);
  }

  onTriangleCellClick(event: { n: number; r: number }): void {
    if (!this.showExpansion() || event.n !== this.power()) {
      return;
    }
    this.activeR.set(this.activeR() === event.r ? null : event.r);
  }

  onTriangleCellHover(event: { n: number; r: number } | null): void {
    if (!this.showExpansion() || !event || event.n !== this.power()) {
      this.hoveredExpansionR.set(null);
      return;
    }
    this.hoveredExpansionR.set(event.r);
  }

  onExpansionMouseover(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement).closest('[data-anchor]');
    if (!anchor) {
      this.hoveredExpansionR.set(null);
      return;
    }
    const match = anchor.getAttribute('data-anchor')!.match(/^(?:term|sym)-(\d+)$/);
    if (match) {
      this.hoveredExpansionR.set(Number(match[1]));
    }
  }

  onExpansionMouseleave(): void {
    this.hoveredExpansionR.set(null);
  }

  onPropertyHover(id: string): void {
    this.activePropertyId.set(id);
  }

  onPropertyLeave(): void {
    this.activePropertyId.set(null);
  }

  onTreePathHover(path: TreePath | null): void {
    this.treeHoveredPath.set(path);
  }
}
